import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateDriver, useUpdateDriver } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Driver, DriverInput } from '../../types';
import { EMPLOYMENT_LABELS, normalizeForm, toOptions } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Checkbox, Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { t } from '../../i18n';

type FormValues = Omit<Record<keyof DriverInput, string>, 'is_active'> & { is_active: boolean };

const EMPTY: FormValues = {
  first_name: '',
  last_name: '',
  employee_id: '',
  department: '',
  phone: '',
  email: '',
  license_number: '',
  license_expiry: '',
  birth_date: '',
  address: '',
  employment_type: '',
  ico: '',
  dic: '',
  is_active: true,
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  driver?: Driver | null;
}

export function DriverFormModal({ isOpen, onClose, driver }: Props) {
  const create = useCreateDriver();
  const update = useUpdateDriver();
  const saving = create.isPending || update.isPending;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: EMPTY });
  const selfEmployed = watch('employment_type') === 'self_employed';

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (!driver) return reset(EMPTY);
    const values = { ...EMPTY };
    for (const key of Object.keys(EMPTY) as (keyof FormValues)[]) {
      (values as Record<string, unknown>)[key] = key === 'is_active' ? driver.is_active : (driver[key] ?? '');
    }
    reset(values);
  }, [isOpen, driver, reset]);

  const onSubmit = async (values: FormValues) => {
    const data = normalizeForm(values) as unknown as DriverInput;
    // IČO and DIČ only apply to self-employed workers
    if (data.employment_type !== 'self_employed') Object.assign(data, { ico: null, dic: null });
    try {
      if (driver) await update.mutateAsync({ id: driver.id, data });
      else await create.mutateAsync(data);
      toast.success(driver ? t('Vodič bol uložený') : t('Vodič bol vytvorený'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Vodiča sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={saving}
      size="lg"
      title={driver ? t('Upraviť vodiča {firstName} {lastName}', { firstName: driver.first_name, lastName: driver.last_name }) : t('Nový vodič')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="driver-form" loading={saving}>
            {driver ? t('Uložiť') : t('Vytvoriť')}
          </Button>
        </>
      }
    >
      <form id="driver-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Input label={t('Meno')} required error={errors.first_name?.message} {...register('first_name', { required: t('Meno je povinné') })} />
        <Input label={t('Priezvisko')} required error={errors.last_name?.message} {...register('last_name', { required: t('Priezvisko je povinné') })} />
        <Input label={t('Osobné číslo')} {...register('employee_id')} />
        <Input label={t('Oddelenie')} {...register('department')} />
        <Input label={t('Telefón')} type="tel" {...register('phone')} />
        <Input
          label={t('E-mail')}
          type="email"
          error={errors.email?.message}
          {...register('email', { pattern: { value: /^\S+@\S+\.\S+$/, message: t('Neplatný e-mail') } })}
        />
        <Input label={t('Číslo vodičského preukazu')} {...register('license_number')} />
        <Input label={t('Platnosť vodičského preukazu')} type="date" {...register('license_expiry')} />

        <div className="border-t border-secondary-200 pt-4 sm:col-span-2">
          <p className="font-medium text-secondary-900">{t('Údaje pre zmluvy')}</p>
          <p className="text-sm text-secondary-500">{t('Použijú sa pri generovaní zmlúv. Nevyplnené údaje zostanú v zmluve vybodkované na ručné doplnenie.')}</p>
        </div>
        <Input label={t('Dátum narodenia')} type="date" {...register('birth_date')} />
        <Select label={t('Typ pracovného vzťahu')} placeholder={t('Nevyplnené')} options={toOptions(EMPLOYMENT_LABELS)} {...register('employment_type')} />
        <Input label={t('Adresa trvalého pobytu')} placeholder={t('Ulica a číslo, PSČ Obec')} wrapperClassName="sm:col-span-2" {...register('address')} />
        {selfEmployed && (
          <>
            <Input label={t('IČO')} inputMode="numeric" {...register('ico')} />
            <Input label={t('DIČ')} inputMode="numeric" {...register('dic')} />
          </>
        )}
        {driver && <Checkbox label={t('Aktívny vodič')} className="sm:col-span-2" {...register('is_active')} />}
      </form>
    </Modal>
  );
}
